<script>
import { mapActions, mapState } from 'vuex';
import { orderBy } from 'lodash';
import { requiredRule } from 'src/utils/helper';
import { eventBus } from 'src/features';

export default {
  props: ['value'],
  data() {
    return {
      PRIORITY_MIN_COUNT: 1,
      PRIORITY_MAX_COUNT: 100,
      loading: false,
      model: {
        id: '',
        name: '',
        description: '',
        active: false,
      },
      priority: this.value.currentSkill?.priority || 1,
      initialSkillId: this.value.currentSkill?.id,
      rules: {
        required: requiredRule,
      },
    };
  },
  async created() {
    this.loading = true;
    try {
      await this.loadSkills();
      if (!this.value.newSkill) {
        this.setFields();
      }
      this.loading = false;
    } catch {
      this.loading = false;
    }
  },
  computed: {
    ...mapState('skillsList', ['skills']),
    ...mapState('skillsDirectories', ['skillsDirectories']),
    mappedSkills() {
      const orderedSkills = orderBy(this.skills, (item) => item.title.toLowerCase());
      return orderedSkills.map((item) => ({
        id: item.id,
        name: item.title,
        active: item.active,
        description: item.desc,
      }));
    },
  },
  methods: {
    ...mapActions('skillsList', ['loadSkills']),
    setFields() {
      const {
        id, name, description, active,
      } = this.value.currentSkill;
      this.model.id = id;
      this.model.name = name;
      this.model.active = active;
      this.model.description = !description ? this.$t('T_NOT_FILLED') : description;
    },
    save() {
      const model = {
        priority: this.priority,
        requestType: {
          ...this.model,
          description: this.model.description === this.$t('T_NOT_FILLED')
            ? null : this.model.description,
        },
      };
      eventBus.$emit('onSaveDirectorySkill', {
        skill: model,
        newSkill: this.value.newSkill,
        initialSkillId: this.initialSkillId,
      });
      this.close();
    },
    close() {
      this.$parent.close([null]);
    },
    isInAnotherDirectoryOfSameTeam(skill) {
      const filteredSkillsDirectories = this.skillsDirectories
        .filter((item) => (item.team.id === this.value.teamId)
          && (item.id !== this.value.currentDirectoryId));
      return filteredSkillsDirectories.some((item) => item.requestTypes
        .some((elem) => elem.requestType.id === skill.id));
    },
    isInCurrentDirectory(skill) {
      return this.value.skillsIdsUsedInThisDirectory.includes(skill.id);
    },
    inWhichDirectoryIsUsed(skill) {
      if (this.isInAnotherDirectoryOfSameTeam(skill)) {
        const filteredSkillsDirectories = this.skillsDirectories
          .filter((item) => (item.team.id === this.value.teamId)
            && (item.id !== this.value.currentDirectoryId));
        const directory = filteredSkillsDirectories.find((item) => item.requestTypes
          .some((elem) => elem.requestType.id === skill.id));
        return directory.name;
      } return '';
    },
    isSkillUnavailable(skill) {
      return this.isInCurrentDirectory(skill)
        || this.isInAnotherDirectoryOfSameTeam(skill);
    },
    onChangeSkillSelect() {
      if (!this.model.description) {
        this.model.description = this.$t('T_NOT_FILLED');
      }
    },
  },
};
</script>

<template lang="pug">
div(:class="$style.root" v-if="!loading")
  header(:class="$style.header")
    div(:class="$style.headerTitle") {{ value.header }}
  div(:class="$style.content")
    v-select(:label="$t('T_NAME')",
      :class="$style.required",
      :rules="[rules.required(model.name)]",
      v-model="model",
      :items="mappedSkills",
      item-value="id",
      item-text="name",
      return-object,
      :item-disabled="isSkillUnavailable"
      :menu-props="{ zIndex: 10009 }"
      required,
      @input="onChangeSkillSelect",
      :no-data-text="$t('T_NO_DATA')")
      template(slot="item", slot-scope="data")
        div(:class="$style.selectItem")
          div(:class="$style.selectItemLeft")
            div(v-if="!data.item.active" :class="$style.visibilityIconWrap")
              ui-icon(:class="[$style.visibilityIcon, $style.disabled]") visibility_off
            div(v-else :class="$style.visibilityIconWrap")
              ui-icon(:class="$style.visibilityIcon") visibility
            div(:class="$style.selectItemOverflow") {{ data.item.name }}
          div(:class="$style.selectItemRight")
            div(:class="$style.selectItemOverflow") {{ inWhichDirectoryIsUsed(data.item) }}
            div(v-if="isInCurrentDirectory(data.item)" :class="$style.selectItemOverflow") {{ value.currentDirectoryName }}
    v-textarea(rows="2", no-resize, :label="$t('T_DESCRIPTION')", v-model="model.description", disabled)
    div(:class="$style.priorityWrap")
      div(:class="$style.priorityLabel") {{ $t('T_PRIORITY') }}
      ui-range(:class="$style.priorityRange" v-model="priority", :min="PRIORITY_MIN_COUNT", :max="PRIORITY_MAX_COUNT", )
      ui-input-number(
        :class="$style.priorityCount",
        :value="priority",
        :min="PRIORITY_MIN_COUNT",
        :max="PRIORITY_MAX_COUNT",
        v-model="priority",
        :zero="false")
  div(:class="$style.action",)
    ui-button(colored="green", :class="$style.save",
      @click.native="save", @keyup.enter.native="save",
      :disabled="!this.model.name") {{ $t('T_SAVE') }}
    ui-button(colored="red", @click.native="close()", @keyup.enter.native="close()") {{ $t('T_CANCEL') }}
</template>

<style lang="sass" module>
.root
  display: block
.header
  padding: 24px
  border-bottom: 1px solid $color_divider
  &Title
    font-size: 18px
    display: flex
    justify-content: center
.content
  display: flex
  flex-direction: column
  padding: 24px 24px 4px
  width: 532px
.selectItem
  display: flex
  width: 500px
  justify-content: space-between
  align-items: center
  &Left
    width: 55%
    margin-right: 10px
    white-space: nowrap
    display: flex
    align-items: center
  &Overflow
    text-overflow: ellipsis
    overflow: hidden
  &Right
    margin-right: 10px
    width: 40%
    white-space: nowrap
.required label::after
  content: "*"
  color: $color_red
.priority
  &Wrap
    display: flex
    align-items: center
    justify-content: space-between
  &Range
    width: 90%
  &Count
    margin-left: 25px
    font-size: 20px
    color: $color_text_second
    vertical-align: baseline
    white-space: nowrap
    width: 50px
    padding: 0
    display: block
    border-width: 0 !important
  &Label
    width: 188px
    font-size: 16px
    margin-right: 20px
    margin: 10px 25px 10px 0
    vertical-align: baseline
    display: flex
    flex-shrink: 0
.action
  display: flex
  margin: 40px
  justify-content: space-around
.visibilityIcon
  width: 24px
  height: 24px
  &Wrap
    width: 24px
    margin: 0 20px 0 10px
</style>
